import { h, Component } from 'preact';
import { ZoomType } from '../../typing/enums';
import Placeholder from '../Placeholder/Placeholder';
import Error from '../Error/Error';
import { withContext } from '../../common/context';
import { FlexCenterStretched } from '../../utils/emotion';
import helpers from './ImageHelpers';
import { isMobile } from 'mobile-device-detect';

import { LazyComponent } from '../LazyComponent/LazyComponent';
const Zoom = LazyComponent(() => import(/* webpackChunkName: "zoom" */ '../Zoom/Zoom'));

class ImageAsset extends Component {
  state = {
    error: false,
    url: undefined,
    zoomUrl: undefined
  };

  componentDidMount() {
    const {
      width,
      height,
      breakpoint,
      zoom,
      context: { cacher }
    } = this.props;
    const adjustedDims = helpers.getAdjustedDims(width, height, breakpoint);
    if (this.props.active) {
      this.initImage(cacher, adjustedDims.width, adjustedDims.height, true, zoom);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      width,
      height,
      breakpoint,
      context,
      context: { config }
    } = this.props;

    const currDims = helpers.getAdjustedDims(prevProps.width, prevProps.height, breakpoint);
    const nextDims = helpers.getAdjustedDims(width || 0, height || 0, breakpoint);

    const isSameAspectRatio = helpers.checkSameRatio(
      currDims.width,
      currDims.height,
      nextDims.width,
      nextDims.height
    );

    const isSameTransformation = this.props.transformation === prevProps.transformation;
    const isActive = this.props.active && prevProps.active === false;

    // Preload zoom image only when zoom is enabled and set to inline/flyout
    const zoomType = this.props.zoom && config.selectZoomPropsType();
    const loadZoomImage = this.props.zoom && zoomType !== ZoomType.POPUP;

    if ((nextDims.width > currDims.width && isSameAspectRatio) || isActive) {
      this.initImage(context.cacher, nextDims.width, nextDims.height, true, loadZoomImage);
    } else if (!isSameAspectRatio || !isSameTransformation) {
      this.resetImage();

      this.initImage(
        context.cacher,
        nextDims.width,
        nextDims.height,
        true,
        loadZoomImage,
        this.getTransformation(prevProps.transformation)
      );
    } else if (this.props.zoom && !prevProps.zoom) {
      this.initImage(context.cacher, nextDims.width, nextDims.height, false, loadZoomImage);
    }
  }

  resetImage() {
    this.setState({ url: undefined });
  }

  initImage(cacher, width, height, loadHighRes, loadZoomRes, transformation) {
    transformation = transformation || this.getTransformation();
    this.setState({ error: false });
    if (loadHighRes) {
      helpers.loadHighResImage(this.props, cacher, width, height, transformation).then(
        asset => this.setState({ url: asset.url }),
        () => this.setState({ error: true })
      );
    }
    if (loadZoomRes) {
      helpers.loadZoomImage(this.props, cacher, width, height, transformation).then(
        asset => this.setState({ zoomUrl: asset.url }),
        () => {
          // if zoom failed load instead the regular url instead of showing an error
          this.setState(this.state.url ? { zoomUrl: this.state.url } : { error: true });
        }
      );
    }
  }

  getTransformation(transformation) {
    if (
      (transformation && transformation !== this.props.transformation) ||
      !this.currTransformation
    ) {
      const nextTransformation = this.props.transformation || transformation;

      this.currTransformation = nextTransformation;
    }

    return this.currTransformation;
  }

  render(props) {
    const { config } = props.context;
    const zoomType = config.selectZoomPropsType();

    return (
      <FlexCenterStretched
        data-test="image-wrap"
        data-ready={!!this.state.url}
        className={props.className}
      >
        {!this.state.error && !this.state.url && (props.width > 150 || props.height > 150) && (
          <Placeholder
            publicId={props.publicId}
            width={props.width}
            height={props.height}
            transformation={this.getTransformation()}
            mediaType={props.mediaType}
          />
        )}
        {this.state.url && !this.state.error ? (
          <FlexCenterStretched relative data-test="img-zoom-wrap">
            <img
              itemprop="image"
              src={this.state.url}
              onLoad={props.onLoad}
              alt={props.alt}
              width={props.width}
              height={props.height}
            />
            {props.zoom && (
              <Zoom
                key={`zoom-${props.publicId}`}
                zoomIn={props.zoomState}
                setZoom={props.setZoom}
                width={props.width}
                height={props.height}
                url={this.state.zoomUrl || this.state.url}
                publicId={props.publicId}
                index={props.index}
                trigger={config.selectZoomPropsTrigger()}
                alt={props.alt}
                type={isMobile && zoomType !== ZoomType.POPUP ? ZoomType.INLINE : zoomType}
                viewerPosition={config.selectZoomPropsViewerPosition()}
                viewerContainer={isMobile ? undefined : config.selectZoomPropsContainer()}
                level={config.selectZoomPropsLevel()}
                onZoomIn={this.onZoomIn}
                onZoomOut={this.onZoomOut}
              />
            )}
          </FlexCenterStretched>
        ) : null}
        {this.state.error ? <Error width={props.width} /> : null}
      </FlexCenterStretched>
    );
  }
}

export default withContext(ImageAsset);
